import groq from "groq";
import type {
	SpotprisenBundle,
	SpotprisenPage,
	ResolvedBlock,
} from "#lib/types/spotprisenContent.js";
import {
	processSeoImage,
	processBlockImages,
	stripStega,
	PAGE_BUILDER_BLOCK_PROJECTION,
} from "./pageBuilderBlocks";

const BLOCKS_PROJECTION = groq`
  blocks[] {
    _key,
    _type,
    ${PAGE_BUILDER_BLOCK_PROJECTION}
  }
`;

const PAGE_PROJECTION = groq`_id, language, area, areaInfo[]{ ... }, blocksOverrideEnabled, seo, ${BLOCKS_PROJECTION}`;

const QUERY_NB = groq`{
  "pages": *[_type=="spotprisen_page" && language=="nb"] { ${PAGE_PROJECTION} }
}`;

const QUERY_LANG_PLUS_NB = groq`{
  "pages":   *[_type=="spotprisen_page" && language==$lang] { ${PAGE_PROJECTION} },
  "pagesNb": *[_type=="spotprisen_page" && language=="nb"]  { ${PAGE_PROJECTION} }
}`;

type SanityFetchClient = {
	fetch: <T = unknown>(query: string, params?: Record<string, unknown>) => Promise<T>;
};

export type RawSpotprisenBundle = {
	pages?: SpotprisenPage[] | null;
	pagesNb?: SpotprisenPage[] | null;
};

/**
 * Surface the GROQ query + params separately so the same string can power
 * the server's one-shot fetch AND the client's `useQuery` subscription in
 * Presentation preview mode. The pattern mirrors `om/aktuelt/[slug]` and
 * `nettbutikk/[product]`.
 */
export const getSpotprisenQuery = (
	lang: string
): { query: string; params: Record<string, string> } => ({
	query: lang === "nb" ? QUERY_NB : QUERY_LANG_PLUS_NB,
	params: { lang },
});

/**
 * In Presentation preview mode the stega-enabled client wraps every string
 * field with invisible Unicode markers (for click-to-edit source mapping).
 * That breaks JS equality on the fields used for lookup — `page.area === "NO4"`,
 * `!page.area`, and `block._type === "faqs"` all misbehave. Strip stega from the
 * lookup-style fields. Block content stays stega-wrapped so the Presentation
 * overlay can highlight editable text.
 *
 * Safe to run unconditionally — `stripStega` is a no-op on strings that have
 * no markers (covers both preview and published paths).
 */
const cleanLookupFields = (page: SpotprisenPage): SpotprisenPage => ({
	...page,
	language: stripStega(page.language),
	area: page.area == null ? page.area : (stripStega(page.area) as SpotprisenPage["area"]),
	// GROQ returns `null` for an unset blocks field; coalesce so the strip
	// and downstream transforms get an Array.
	blocks: ((page.blocks ?? []) as ResolvedBlock[]).map((b) => ({
		...b,
		_type: stripStega(b._type),
	})) as ResolvedBlock[],
});

const processPageImages = (page: SpotprisenPage): SpotprisenPage => ({
	...page,
	// Resolve the SEO image alongside the block images so `seo.image.src` is
	// usable by MetaData on both the SSR path and the live-update path.
	seo: page.seo?.image ? { ...page.seo, image: processSeoImage(page.seo.image) } : page.seo,
	blocks: page.blocks.map(processBlockImages),
});

const normalisePage = (page: SpotprisenPage): SpotprisenPage =>
	processPageImages(cleanLookupFields(page));

/**
 * Pure transform from the raw GROQ result to the final bundle. Safe to call
 * client-side — apply this to `useQuery`'s emitted data on each update.
 */
export const normaliseSpotprisenBundle = (
	raw: RawSpotprisenBundle | null | undefined,
	lang: string
): SpotprisenBundle => {
	const pages = (raw?.pages ?? []).map(normalisePage);
	if (lang === "nb") {
		return { pages, pagesNb: pages };
	}
	return {
		pages,
		pagesNb: (raw?.pagesNb ?? []).map(normalisePage),
	};
};

export const fetchSpotprisenContent = async (
	client: SanityFetchClient,
	lang: string
): Promise<SpotprisenBundle> => {
	const { query, params } = getSpotprisenQuery(lang);
	const raw = await client.fetch<RawSpotprisenBundle>(query, params);
	return normaliseSpotprisenBundle(raw, lang);
};
